import { ref, watch, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import router from '@/router/index'

// Notification

export default function useActivityTypesList() {
  // Use toast
  const toast = useToast()
  const route = router.history.current

  const refActivityTypesTable = ref(null)

  // Table Handlers
  const tableColumns = [
    {
      key: 'typeID',
      label: 'type',
      sortable: true,
    },
    {
      key: 'typeName',
      label: 'activity type',
      sortable: true,
    },
    {
      key: 'timeSlots',
      label: 'current availability scheme',
      sortable: false,
    },
    {
      key: 'customerBookable',
      label: 'show in portal',
      sortable: true,
    },
    {
      key: 'prospectBookable',
      label: 'customers only',
      sortable: true,
    },
    {
      key: 'customFields',
      label: 'custom fields',
      sortable: true,
    },
    { key: 'status', sortable: true },
    { key: 'actions' },
  ]
  const perPage = ref(route.query.perPage || process.env.VUE_APP_PER_PAGE_CONTENT)
  const totalActivityTypes = ref(0)
  const currentPage = ref(route.query.page || 1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref(route.query.search || '')
  const sortBy = ref(route.query.sortBy || 'typeID')
  const isSortDirDesc = ref(route.query.sortDesc === 'true')
  const statusFilter = ref([])
  if (Array.isArray(route.query.status)) {
    statusFilter.value = route.query.status
  } else if (route.query.status) {
    statusFilter.value = [route.query.status]
  }
  const customerBookable = ref(route.query.customerBookable || null)
  const prospectBookable = ref(route.query.prospectBookable || null)
  const toDeletedID = ref(null)
  let initialLoad = true
  const currentDataLength = ref(0)

  const dataMeta = computed(() => {
    const localItemsCount = refActivityTypesTable.value ? refActivityTypesTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalActivityTypes.value,
    }
  })

  const refetchData = () => {
    refActivityTypesTable.value.refresh()
  }

  watch([searchQuery, statusFilter, customerBookable, prospectBookable], () => {
    currentPage.value = 1
    refetchData()
  }, { deep: true })

  watch([currentPage], () => {
    refetchData()
  }, { deep: true })

  const fetchActivityTypes = (ctx, callback) => {
    store
      .dispatch('app-activity-types/fetchActivityTypes', {
        search: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        status: statusFilter.value,
        customerBookable: customerBookable.value,
        prospectBookable: prospectBookable.value,
      },
      { root: true })
      .then(response => {
        const { activityTypes, total } = response.data
        currentDataLength.value = activityTypes.length
        callback(activityTypes)
        totalActivityTypes.value = total
        if (!initialLoad) {
          router.replace({
            query: {
              search: searchQuery.value,
              perPage: perPage.value,
              page: currentPage.value,
              sortBy: sortBy.value,
              sortDesc: isSortDirDesc.value,
              status: statusFilter.value,
              customerBookable: customerBookable.value,
              prospectBookable: prospectBookable.value,
            },
          }).catch(() => {})
        }
        initialLoad = false
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const deleteActivityType = () => {
    store
      .dispatch('app-activity-types/deleteActivityType', {
        id: toDeletedID.value,
      },
      { root: true })
      .then(response => {
        refetchData()
        toast({
          component: ToastificationContent,
          props: {
            title: response.data.message || '',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveActivityTypeStatusVariant = status => {
    if (status === 'published') return 'success'
    if (status === 'unpublished') return 'warning'
    if (status === 'draft') return 'secondary'
    return 'primary'
  }

  return {
    fetchActivityTypes,
    tableColumns,
    perPage,
    currentPage,
    totalActivityTypes,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refActivityTypesTable,

    resolveActivityTypeStatusVariant,
    refetchData,
    currentDataLength,

    toDeletedID,
    deleteActivityType,

    // Extra Filters
    statusFilter,
    customerBookable,
    prospectBookable,
  }
}
